import React from 'react'
import {
  Button,
  Flex,
  Input,
  Spacer,
  Text,
  Icon,
} from '@sendoutcards/quantum-design-ui'
import { useState, useActions } from 'src/hooks'
import { useUpdateAccount } from 'src/react_query'
import { parseError } from 'src/utils/parseError'
import { ResetPasswordRoute } from './routes/ResetPassword'

type ResetPasswordProps = {
  route?: ResetPasswordRoute
}

const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const updateAccountMutation = useUpdateAccount()
  const actions = useActions()

  const validatePasswords = (): boolean => {
    setErrorMessage(null)

    if (!password || !confirmPassword) {
      setErrorMessage('Both password fields are required.')
      return false
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.')
      return false
    }

    return true
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validatePasswords()) {
      return
    }

    setErrorMessage(null)

    try {
      const result = await updateAccountMutation.mutateAsync({
        account: { password },
      })

      if (result.updateAccount.account) {
        actions.updatedAccount(result.updateAccount.account)
        actions.openAccount()
      }
    } catch (error) {
      setErrorMessage(
        parseError(error, 'Failed to reset password. Please try again.'),
      )
    }
  }

  const isFormDisabled = updateAccountMutation.isPending

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '20px',
      }}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ maxWidth: '500px', width: '100%' }}
      >
        <Spacer space="x4" />
        <Icon name="lock" size={48} primaryColor="primary" />
        <Spacer space="x3" />
        <Text type="title" alignment="center" weight="bold">
          Reset Your Password
        </Text>
        <Spacer space="x3" />
        <Text type="body" alignment="center">
          For security reasons, you must reset your password before continuing.
          Please enter a new password to regain access to your account.
        </Text>
        <Spacer space="x4" />

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Flex
            flexDirection="column"
            alignItems="center"
            style={{ width: '100%' }}
          >
            {errorMessage && (
              <>
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#FEE2E2',
                    borderRadius: '8px',
                    border: '1px solid #FCA5A5',
                    marginBottom: '24px',
                  }}
                >
                  <Text type="body" color="danger" alignment="center">
                    {errorMessage}
                  </Text>
                </Flex>
              </>
            )}

            <div style={{ width: '100%', marginBottom: '16px' }}>
              <Input
                type="password"
                onChange={setPassword}
                value={password}
                placeholder="New Password"
                isFullWidth={true}
                autoComplete="new-password"
                isDisabled={isFormDisabled}
              />
            </div>

            <div style={{ width: '100%', marginBottom: '24px' }}>
              <Input
                type="password"
                onChange={setConfirmPassword}
                value={confirmPassword}
                placeholder="Confirm New Password"
                isFullWidth={true}
                autoComplete="new-password"
                isDisabled={isFormDisabled}
              />
            </div>

            <Button
              fullWidth
              size="large"
              onClick={'submitForm'}
              disabled={
                updateAccountMutation.isPending ||
                updateAccountMutation.isLoading
              }
            >
              {updateAccountMutation.isPending ||
              updateAccountMutation.isLoading
                ? 'Resetting Password...'
                : 'Reset Password'}
            </Button>

            <Spacer space="x3" />
            <Text type="caption" alignment="center">
              Please enter a new password to continue using your account.
            </Text>
          </Flex>
        </form>
      </Flex>
    </div>
  )
}

export default ResetPassword
